import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaTimes, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import './style.css';

const ChangePasswordModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords({
            ...showPasswords,
            [field]: !showPasswords[field]
        });
    };

    const validatePassword = (password) => {
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
        const isLongEnough = password.length >= 8;

        return {
            isValid: hasUpperCase && hasLowerCase && hasNumber && isLongEnough,
            errors: {
                length: !isLongEnough,
                uppercase: !hasUpperCase,
                lowercase: !hasLowerCase,
                number: !hasNumber
            }
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Client-side validation
        if (formData.newPassword !== formData.confirmPassword) {
            toast.error(t('changePassword.mismatch'));
            setLoading(false);
            return;
        }

        const validation = validatePassword(formData.newPassword);
        if (!validation.isValid) {
            toast.error(t('changePassword.invalid'));
            setLoading(false);
            return;
        }

        try {
            const accessToken = localStorage.getItem('access_token');
            // Remove /api/v1 from the base URL if it exists, we'll add it explicitly
            const baseUrl = process.env.NODE_ENV === 'production'
                ? '/api'
                : (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/api\/v1$/, '');

            const response = await fetch(`${baseUrl}/api/v1/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    current_password: formData.currentPassword,
                    new_password: formData.newPassword,
                    confirm_password: formData.confirmPassword
                })
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMessage = data.error?.message || data.message || t('changePassword.failed');
                throw new Error(errorMessage);
            }

            toast.success(t('changePassword.success'));
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="modal-title">
                        <FaLock />
                        <h2>{t('changePassword.title')}</h2>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="change-password-form">
                    <div className="form-group">
                        <label htmlFor="currentPassword">{t('changePassword.currentPassword')}</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPasswords.current ? "text" : "password"}
                                id="currentPassword"
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                placeholder={t('changePassword.enterCurrent')}
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => togglePasswordVisibility('current')}
                            >
                                {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="newPassword">{t('changePassword.newPassword')}</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPasswords.new ? "text" : "password"}
                                id="newPassword"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                placeholder={t('changePassword.enterNew')}
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => togglePasswordVisibility('new')}
                            >
                                {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                        <small className="form-hint">
                            {t('changePassword.hint')}
                        </small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">{t('changePassword.confirmPassword')}</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPasswords.confirm ? "text" : "password"}
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                placeholder={t('changePassword.confirmNew')}
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => togglePasswordVisibility('confirm')}
                            >
                                {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={onClose}
                            disabled={loading}
                        >
                            {t('changePassword.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={loading}
                        >
                            {loading ? t('changePassword.submitting') : t('changePassword.submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChangePasswordModal;
